import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useResetInfo, useResetPassword } from "../api/hooks";
import { Loading } from "../components/Layout";
import { Splash } from "../components/Splash";

/** A one-time password reset link (/reset/<token>) from an admin: choose a new password, then you're signed in. */
export function ResetPage() {
  const token = useParams().token ?? "";
  const info = useResetInfo(token);
  const reset = useResetPassword(token);
  const navigate = useNavigate();
  const [f, setF] = useState({ next: "", confirm: "" });
  const [problem, setProblem] = useState("");
  const [inactive, setInactive] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (f.next !== f.confirm) return setProblem("The two passwords don't match.");
    setProblem("");
    reset.mutate(f.next, {
      onSuccess: (state) => (state.user ? navigate("/", { replace: true }) : setInactive(true)),
      onError: (err) => setProblem(err.message),
    });
  };

  let body;
  if (info.isPending)
    body = (
      <div className="splash-form">
        <Loading />
      </div>
    );
  else if (inactive)
    body = (
      <div className="splash-form">
        <h2>Password changed</h2>
        <p className="muted">
          Your account isn&apos;t active at the moment, so ask an admin before signing in.
        </p>
      </div>
    );
  else if (info.error || !info.data)
    body = (
      <div className="splash-form">
        <h2>This link doesn&apos;t work</h2>
        <p className="muted">It has expired or has already been used. Ask an admin for a new one.</p>
        <Link className="btn" to="/login">
          Go to sign in
        </Link>
      </div>
    );
  else
    body = (
      <form className="splash-form" onSubmit={submit}>
        <h2>Choose a new password</h2>
        <p className="small muted">For {info.data.email}. This link works once.</p>
        <div>
          <label htmlFor="reset-pw">New password</label>
          <input
            id="reset-pw"
            type="password"
            required
            autoFocus
            minLength={8}
            autoComplete="new-password"
            value={f.next}
            onChange={(e) => setF((x) => ({ ...x, next: e.target.value }))}
          />
          <p className="small muted field-hint">At least 8 characters.</p>
        </div>
        <div>
          <label htmlFor="reset-pw2">Confirm new password</label>
          <input
            id="reset-pw2"
            type="password"
            required
            autoComplete="new-password"
            value={f.confirm}
            onChange={(e) => setF((x) => ({ ...x, confirm: e.target.value }))}
          />
        </div>
        {problem && (
          <p className="small error-text" role="alert">
            {problem}
          </p>
        )}
        <button className="btn" type="submit" disabled={reset.isPending}>
          Save and sign in
        </button>
      </form>
    );

  return (
    <Splash>
      <div className="splash-card panel">{body}</div>
    </Splash>
  );
}
